import { useState } from 'react'
import { Copy, Check, Info, X, Globe } from 'lucide-react'
import { copyText } from '../lib/externalLink'

interface ExternalLinkDialogProps {
  url: string
  /** 关闭弹窗 */
  onClose: () => void
}

/**
 * 「复制链接到浏览器打开」引导弹窗。
 *
 * 为什么需要它：微信内（特别是小程序 web-view）打不开未配置业务域名的外部网页，
 * 硬跳会白屏或报「不支持打开非业务域名」。此时唯一稳妥的做法是把链接交给用户，
 * 让他到系统浏览器打开。
 */
const ExternalLinkDialog = ({ url, onClose }: ExternalLinkDialogProps) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    const ok = await copyText(url)
    if (!ok) return
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2500)
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2.5 px-4 pt-4">
          <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-semibold text-gray-900">请在浏览器中打开</h3>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              微信内无法直接打开该链接，请复制后到手机或电脑浏览器打开。
            </p>
          </div>
          <button onClick={onClose} className="p-1 -mt-0.5 text-gray-300 hover:text-gray-500" aria-label="关闭">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 链接展示：保持可选中，复制按钮失效时用户还能长按手动复制 */}
        <div className="px-4 mt-3">
          <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2">
            <Globe className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="text-[11px] text-gray-600 break-all select-all leading-relaxed font-mono">{url}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4">
          <button
            onClick={handleCopy}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors ${
              copied ? 'bg-green-50 text-green-700' : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? '已复制，去浏览器粘贴' : '复制链接'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-600 bg-gray-100 hover:bg-gray-200"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  )
}

export default ExternalLinkDialog
